import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Share2, 
  Globe,
  Megaphone,
  Newspaper,
  MessageSquare,
  ArrowLeft,
  Image,
  FileText,
  Play,
  Download,
  FolderOpen
} from 'lucide-react';
import { useData } from '../context/DataContext';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get correct URL (handles both Cloudinary and local URLs)
const getFileUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const DisseminationSection = ({ setActiveTab }) => {
  const { dissemination, loading } = useData();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);

  // Fetch uploaded impact files
  useEffect(() => {
    const fetchFiles = async () => {
      setFilesLoading(true);
      try {
        const files = await api.getFilesByCategory('impact');
        setUploadedFiles(files);
      } catch (err) {
        console.error('Error fetching impact files:', err);
        setUploadedFiles([]);
      } finally {
        setFilesLoading(false);
      }
    };
    fetchFiles();
  }, []);

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="w-5 h-5" />;
      case 'video': return <Play className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getChannelIcon = (name) => {
    switch (name.toLowerCase()) {
      case 'social media':
        return <Share2 className="w-5 h-5" />;
      case 'etwinning platform':
        return <Globe className="w-5 h-5" />;
      case 'local media':
        return <Newspaper className="w-5 h-5" />;
      case 'educational networks':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <Megaphone className="w-5 h-5" />;
    }
  };

  if (loading || !dissemination) {
    return (
      <section className="min-h-[calc(100vh-80px)] py-12 bg-white flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="min-h-[calc(100vh-80px)] py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('home')}
          className="flex items-center gap-2 text-[#3d6a99] hover:text-[#1e3a5f] mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
            Dissemination & Impact
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Spreading the Impact
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {dissemination.introduction}
          </p>
        </div>

        {/* Impact Metrics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {(dissemination.impact || []).map((item, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] rounded-2xl p-6 text-center hover:scale-105 transition-all duration-300 shadow-lg"
            >
              <div className="text-4xl sm:text-5xl font-bold text-white mb-2">
                {item.value}
              </div>
              <div className="text-[#90cdf4] font-semibold mb-1">
                {item.metric}
              </div>
              <div className="text-white/70 text-sm">
                {item.description}
              </div>
            </div>
          ))}
        </div>

        {/* Dissemination Channels */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-[#1e3a5f] mb-8 text-center">
            Dissemination Channels
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(dissemination.channels || []).map((channel, index) => (
              <div
                key={index}
                className="group flex items-start gap-4 p-5 bg-[#f8fafc] rounded-xl border border-[#e2e8f0] hover:bg-[#f0f7ff] hover:border-[#2c5282]/30 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  {getChannelIcon(channel.name)}
                </div>
                <div>
                  <h4 className="font-semibold text-[#1e3a5f] mb-1">
                    {channel.name}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {channel.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Events Timeline */}
        <div className="bg-[#f8fafc] rounded-3xl p-8 sm:p-12">
          <h3 className="text-2xl font-bold text-[#1e3a5f] mb-8 text-center flex items-center justify-center gap-3">
            <Calendar className="w-6 h-6" />
            Key Events
          </h3>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-0.5 bg-[#2c5282]/20 transform sm:-translate-x-1/2" />
            
            <div className="space-y-8">
              {(dissemination.events || []).map((event, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col sm:flex-row gap-4 ${
                    index % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'
                  }`}
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-4 sm:left-1/2 w-4 h-4 bg-[#1e3a5f] rounded-full transform -translate-x-1/2 border-4 border-white shadow-md z-10" />
                  
                  {/* Content */}
                  <div className={`flex-1 ml-10 sm:ml-0 ${
                    index % 2 === 0 ? 'sm:pr-12 sm:text-right' : 'sm:pl-12'
                  }`}>
                    <div className={`bg-white rounded-xl p-5 shadow-sm border border-[#e2e8f0] hover:shadow-lg transition-all duration-300 inline-block ${
                      index % 2 === 0 ? 'sm:float-right' : 'sm:float-left'
                    }`}>
                      <span className="inline-block px-3 py-1 bg-[#f0f7ff] text-[#1e3a5f] rounded-full text-xs font-semibold mb-2">
                        {event.date}
                      </span>
                      <h4 className="font-bold text-[#1e3a5f] mb-1">
                        {event.title}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {event.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Empty space for alignment */}
                  <div className="hidden sm:block flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Uploaded Media Section */}
        {uploadedFiles.length > 0 && (
          <div className="mt-16" data-testid="impact-media-section">
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-8 text-center flex items-center justify-center gap-3">
              <FolderOpen className="w-6 h-6" />
              Media & Documentation
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {uploadedFiles.map((file) => (
                <a
                  key={file.id}
                  href={getFileUrl(file.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-xl border border-[#e2e8f0] overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`impact-file-${file.id}`}
                >
                  {/* Preview */}
                  {file.file_type === 'image' ? (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img 
                        src={getFileUrl(file.url)} 
                        alt={file.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <div className={`aspect-video flex items-center justify-center ${
                      file.file_type === 'video' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                        file.file_type === 'video' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                      }`}>
                        {getFileIcon(file.file_type)}
                      </div>
                    </div>
                  )}
                  
                  {/* Info */}
                  <div className="p-4">
                    <h4 className="font-medium text-[#1e3a5f] truncate group-hover:text-[#2c5282] transition-colors">
                      {file.title || file.original_name}
                    </h4>
                    {file.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{file.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400 uppercase">{file.file_type}</span>
                      <span className="inline-flex items-center gap-1 text-xs text-[#1e3a5f] font-medium">
                        <Download className="w-3 h-3" />
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DisseminationSection;
